import React, { useEffect, useRef, useState } from 'react';
import { useGridData, useFaults } from '../hooks/useGridData';
import { api } from '../api/gridApi';

export default function Dashboard() {
  const { gridData } = useGridData(5000);
  const { faults } = useFaults();

  const chartRef = useRef(null);
  const mixRef = useRef(null);
  const mobileChartRef = useRef(null);

  const chartInst = useRef(null);
  const mixInst = useRef(null);
  const mobileChartInst = useRef(null);

  const [mix, setMix] = useState({
    solar: 54,
    wind: 26,
    hydro: 12,
    thermal: 8,
  });

  useEffect(() => {
    api.getEnergyMix()
      .then((r) => setMix(r.data))
      .catch((e) => console.log('Energy mix error:', e));

    const interval = setInterval(() => {
      api.getEnergyMix()
        .then((r) => setMix(r.data))
        .catch((e) => console.log('Energy mix error:', e));
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const { Chart, registerables } = await import('chart.js');

        if (cancelled) return;

        Chart.register(...registerables);

        const labels = Array.from(
          { length: 24 },
          (_, i) => `${i}:00`
        );

        /*
          IMPORTANT:
          These values are demonstration/simulated chart values.
          They must not be presented as independently measured grid telemetry.
        */
        const actual = labels.map((_, i) =>
          i <= new Date().getHours()
            ? parseFloat(
                (
                  2.8 +
                  Math.sin((i - 6) * Math.PI / 12) * 0.8 +
                  Math.random() * 0.15
                ).toFixed(2)
              )
            : null
        );

        const forecast = labels.map((_, i) =>
          parseFloat(
            (
              2.8 +
              Math.sin((i - 6) * Math.PI / 12) * 0.85 +
              Math.random() * 0.1
            ).toFixed(2)
          )
        );

        /* DESKTOP LOAD CHART */
        if (chartRef.current) {
          if (chartInst.current) {
            chartInst.current.destroy();
            chartInst.current = null;
          }

          chartInst.current = new Chart(chartRef.current, {
            type: 'line',
            data: {
              labels,
              datasets: [
                {
                  label: 'Simulated (GW) — illustrative, not live telemetry',
                  data: actual,
                  borderColor: 'rgba(0,220,100,1)',
                  backgroundColor: 'rgba(0,220,100,0.15)',
                  tension: 0.4,
                  fill: true,
                  pointRadius: 0,
                  borderWidth: 2,
                },
                {
                  label: 'Simulated forecast (GW) — not validated',
                  data: forecast,
                  borderColor: 'rgba(245,166,35,1)',
                  backgroundColor: 'transparent',
                  tension: 0.4,
                  borderDash: [4, 4],
                  pointRadius: 0,
                  borderWidth: 1.5,
                },
              ],
            },
            options: {
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: {
                  display: true,
                  position: 'top',
                  labels: {
                    boxWidth: 12,
                    padding: 14,
                    color: 'rgba(238,244,240,0.45)',
                    font: {
                      family: "'IBM Plex Mono',monospace",
                      size: 10,
                    },
                  },
                },
                tooltip: {
                  backgroundColor: '#111f14',
                  borderColor: 'rgba(0,220,100,0.25)',
                  borderWidth: 1,
                  titleColor: '#00dc64',
                  bodyColor: 'rgba(238,244,240,0.7)',
                },
              },
              scales: {
                x: {
                  grid: {
                    color: 'rgba(0,220,100,0.08)',
                  },
                  ticks: {
                    color: 'rgba(238,244,240,0.35)',
                    font: { size: 9 },
                  },
                },
                y: {
                  grid: {
                    color: 'rgba(0,220,100,0.08)',
                  },
                  ticks: {
                    color: 'rgba(238,244,240,0.35)',
                    font: { size: 9 },
                  },
                  min: 1.5,
                  max: 4.5,
                },
              },
            },
          });
        }

        /* DESKTOP ENERGY MIX CHART */
        if (mixRef.current) {
          if (mixInst.current) {
            mixInst.current.destroy();
            mixInst.current = null;
          }

          mixInst.current = new Chart(mixRef.current, {
            type: 'doughnut',
            data: {
              labels: ['Solar', 'Wind', 'Hydro', 'Thermal'],
              datasets: [
                {
                  data: [
                    mix.solar,
                    mix.wind,
                    mix.hydro,
                    mix.thermal,
                  ],
                  backgroundColor: [
                    'rgba(0,220,100,1)',
                    'rgba(77,166,255,0.8)',
                    'rgba(0,180,255,0.6)',
                    'rgba(245,166,35,1)',
                  ],
                  borderWidth: 0,
                },
              ],
            },
            options: {
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: {
                  display: false,
                },
              },
              cutout: '72%',
            },
          });
        }

        /* MOBILE COMPACT FORECAST CHART */
        if (mobileChartRef.current) {
          if (mobileChartInst.current) {
            mobileChartInst.current.destroy();
            mobileChartInst.current = null;
          }

          mobileChartInst.current = new Chart(
            mobileChartRef.current,
            {
              type: 'line',
              data: {
                labels,
                datasets: [
                  {
                    label: 'Observed demo series',
                    data: actual,
                    borderColor: 'rgba(0,220,100,1)',
                    backgroundColor: 'rgba(0,220,100,0.10)',
                    tension: 0.4,
                    fill: true,
                    pointRadius: 0,
                    borderWidth: 2,
                  },
                  {
                    label: 'Forecast estimate',
                    data: forecast,
                    borderColor: 'rgba(245,166,35,1)',
                    backgroundColor: 'transparent',
                    tension: 0.4,
                    borderDash: [4, 4],
                    pointRadius: 0,
                    borderWidth: 1.5,
                  },
                ],
              },
              options: {
                responsive: true,
                maintainAspectRatio: false,
                interaction: {
                  intersect: false,
                  mode: 'index',
                },
                plugins: {
                  legend: {
                    display: false,
                  },
                  tooltip: {
                    backgroundColor: '#111f14',
                    borderColor: 'rgba(0,220,100,0.25)',
                    borderWidth: 1,
                    titleColor: '#00dc64',
                    bodyColor: 'rgba(238,244,240,0.75)',
                  },
                },
                scales: {
                  x: {
                    grid: {
                      display: false,
                    },
                    ticks: {
                      color: 'rgba(238,244,240,0.28)',
                      font: {
                        size: 8,
                      },
                      maxTicksLimit: 6,
                    },
                  },
                  y: {
                    grid: {
                      color: 'rgba(0,220,100,0.06)',
                    },
                    ticks: {
                      color: 'rgba(238,244,240,0.28)',
                      font: {
                        size: 8,
                      },
                      maxTicksLimit: 4,
                    },
                    min: 1.5,
                    max: 4.5,
                  },
                },
              },
            }
          );
        }
      } catch (e) {
        console.log('Chart error:', e);
      }
    };

    load();

    return () => {
      cancelled = true;

      if (chartInst.current) {
        chartInst.current.destroy();
        chartInst.current = null;
      }

      if (mixInst.current) {
        mixInst.current.destroy();
        mixInst.current = null;
      }

      if (mobileChartInst.current) {
        mobileChartInst.current.destroy();
        mobileChartInst.current = null;
      }
    };
  }, [mix]);

  const safeFaults = Array.isArray(faults) ? faults : [];

  const activeFaults = safeFaults.filter(
    (f) => f.severity !== 'ok'
  );

  const criticalFaults = safeFaults.filter(
    (f) => f.severity === 'critical'
  );

  const warningFaults = safeFaults.filter(
    (f) => f.severity === 'warning'
  );

  const currentLoad = Number(gridData?.load || 3.42);

  const forecastBars = [
    { label: 'Now', val: currentLoad },
    { label: '6h', val: parseFloat((currentLoad + 0.2).toFixed(2)) },
    { label: '12h', val: parseFloat((currentLoad + 0.45).toFixed(2)) },
    { label: '24h', val: parseFloat((currentLoad + 0.68).toFixed(2)) },
    { label: '36h', val: parseFloat((currentLoad + 0.78).toFixed(2)) },
    { label: '48h', val: parseFloat((currentLoad + 0.95).toFixed(2)) },
    { label: '72h', val: parseFloat((currentLoad + 0.88).toFixed(2)) },
  ];

  const mixBars = [
    { label: 'Solar', pct: mix.solar, cls: '' },
    { label: 'Wind', pct: mix.wind, cls: '' },
    { label: 'Hydro', pct: mix.hydro, cls: '' },
    { label: 'Thermal', pct: mix.thermal, cls: 'high' },
  ];

  const isLive = gridData?.source === 'openmeteo_live';

  const sourceLabel = isLive
    ? 'WEATHER-DERIVED INPUT'
    : 'SIMULATED ENVIRONMENT';

  const systemState =
    criticalFaults.length > 0
      ? 'Critical attention required'
      : warningFaults.length > 0
        ? 'Caution conditions detected'
        : 'No critical conditions detected';

  const systemStateClass =
    criticalFaults.length > 0
      ? 'critical'
      : warningFaults.length > 0
        ? 'warning'
        : 'normal';

  const getOperationalInsight = () => {
    if (criticalFaults.length > 0) {
      return `${criticalFaults.length} critical event${
        criticalFaults.length > 1 ? 's require' : ' requires'
      } immediate review. Prioritize affected assets before relying on broader demand projections.`;
    }

    if (warningFaults.length > 0) {
      return `${warningFaults.length} warning condition${
        warningFaults.length > 1 ? 's are' : ' is'
      } currently active. Demand is estimated to rise over the next 12 hours, so affected assets should be monitored for deterioration.`;
    }

    if (currentLoad >= 4) {
      return 'Estimated demand is elevated relative to the current demonstration baseline. No critical events are presently identified, but load conditions warrant closer observation.';
    }

    return 'No critical events are presently identified. The current model outlook indicates rising demand over the next 12 hours, with uncertainty increasing further into the forecast horizon.';
  };

  return (
    <>
      <div className="desktop-dashboard-only">
        <div className="page">
          <div className="page-header">
            <div>
              <div className="page-title">
                Grid <span>Dashboard</span>
              </div>

              <div className="page-sub">
                {isLive
                  ? `Live weather · ${gridData?.location || 'India'} · ${gridData?.temperature}°C · Auto-refresh every 5s`
                  : 'Simulated data · Auto-refresh every 5s'}
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-solid">
                Export Report
              </button>
            </div>
          </div>

          <div className="stat-row">
            <div className="stat-card">
              <div className="sc-label">Total Grid Load</div>
              <div className="sc-val">{gridData?.load || '—'}</div>
              <div className="sc-change up">▲ Weather-adjusted demand</div>
              <div className="sc-sub">
                GW · {gridData?.location || 'India National Grid'}
              </div>
            </div>

            <div className="stat-card info">
              <div className="sc-label">Solar Generation</div>
              <div className="sc-val">{gridData?.solar || '—'}</div>
              <div className="sc-change up">
                ▲ {gridData?.radiation || 0} W/m² radiation
              </div>
              <div className="sc-sub">
                MW · {isLive ? 'Real weather data' : 'Simulated'}
              </div>
            </div>

            <div className="stat-card warn">
              <div className="sc-label">Carbon Intensity</div>
              <div className="sc-val">{gridData?.carbon || '—'}</div>
              <div className="sc-change down">▼ Solar-adjusted</div>
              <div className="sc-sub">
                gCO₂/kWh · {gridData?.temperature ? `${gridData.temperature}°C outside` : ''}
              </div>
            </div>

            <div className="stat-card">
              <div className="sc-label">Wind Speed</div>
              <div className="sc-val">{gridData?.windspeed || '—'}</div>
              <div className="sc-change up">
                ▲ {gridData?.wind || 0} MW wind generation
              </div>
              <div className="sc-sub">
                km/h · {isLive ? 'Live sensor' : 'Simulated'}
              </div>
            </div>
          </div>

          <div className="grid3">
            <div className="panel">
              <div className="panel-header">
                <span className="panel-title">24-Hour Load vs Forecast</span>
                <span className="panel-badge pb-amber">
                  Simulated
                </span>
              </div>

              <div style={{ position: 'relative', height: 240 }}>
                <canvas ref={chartRef} />
              </div>
              <div style={{ marginTop: 8, fontSize: 10, color: 'var(--muted2)', fontFamily: 'var(--mono)' }}>
                SIMULATED — both lines are illustrative and not live telemetry or a validated forecast.
              </div>
            </div>

            <div className="panel">
              <div className="panel-header">
                <span className="panel-title">Energy Mix</span>
                <span className="panel-badge pb-blue">
                  {mix.source === 'weather_adjusted' ? 'Weather-adjusted' : 'Now'}
                </span>
              </div>

              <div style={{ position: 'relative', height: 130 }}>
                <canvas ref={mixRef} />
              </div>

              <div className="forecast-bars" style={{ marginTop: 12 }}>
                {mixBars.map((m) => (
                  <div className="fbar" key={m.label}>
                    <span className="fbar-label">{m.label}</span>

                    <div className="fbar-track">
                      <div
                        className={`fbar-fill ${m.cls}`}
                        style={{ width: `${m.pct}%` }}
                      />
                    </div>

                    <span className="fbar-val">{m.pct}%</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="grid2">
            <div className="panel">
              <div className="panel-header">
                <span className="panel-title">Active Faults</span>
                <span className="panel-badge pb-red">
                  {activeFaults.length} Active
                </span>
              </div>

              <div className="fault-list">
                {safeFaults.length === 0 ? (
                  <div style={{ padding: '24px 4px', color: 'var(--muted2)', fontSize: 12 }}>
                    No live fault data — demo environment.
                  </div>
                ) : (
                  safeFaults.slice(0, 4).map((f, index) => (
                    <div
                      className="fault-item"
                      key={`desktop-fault-${f.id ?? 'unknown'}-${f.name ?? 'fault'}-${index}`}
                    >
                      <div
                        className={`fault-dot fd-${
                          f.severity === 'critical'
                            ? 'red'
                            : f.severity === 'warning'
                              ? 'amber'
                              : 'green'
                        }`}
                      />

                      <div>
                        <div className="fault-info-name">{f.name}</div>
                        <div className="fault-info-loc">{f.location}</div>
                      </div>

                      <div>
                        <div className="fault-time">{f.time}</div>

                        <div
                          className={`fault-status fs-${
                            f.severity === 'critical'
                              ? 'critical'
                              : f.severity === 'warning'
                                ? 'warning'
                                : 'ok'
                          }`}
                        >
                          {f.severity}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="panel">
              <div className="panel-header">
                <span className="panel-title">72-Hour Demand Forecast</span>
                <span className="panel-badge pb-amber">Illustrative</span>
              </div>

              <div className="forecast-bars">
                {forecastBars.map((b) => {
                  const pct = Math.round((b.val / 5) * 100);

                  return (
                    <div className="fbar" key={b.label}>
                      <span className="fbar-label">{b.label}</span>

                      <div className="fbar-track">
                        <div
                          className={`fbar-fill ${
                            pct > 85
                              ? 'critical'
                              : pct > 75
                                ? 'high'
                                : ''
                          }`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>

                      <span className="fbar-val">{b.val} GW</span>
                    </div>
                  );
                })}
              </div>
              <div style={{ marginTop: 8, fontSize: 10, color: 'var(--muted2)', fontFamily: 'var(--mono)' }}>
                Illustrative projection — not a validated forecast.
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mobile-dashboard-only">
        <div className="mobile-command-page">

          <section className="mobile-command-hero">
            <div className="mobile-command-eyebrow">
              GRID OVERVIEW
            </div>

            <h1>
              Operational
              <span> Intelligence</span>
            </h1>

            <p>
              Evidence-aware monitoring for electricity distribution systems.
            </p>

            <div className="mobile-source-strip">
              <span className="mobile-source-dot" />
              <span>{sourceLabel}</span>

              {gridData?.location && (
                <span className="mobile-source-location">
                  {gridData.location}
                </span>
              )}
            </div>
          </section>

          <section className="mobile-primary-card">
            <div className="mobile-card-topline">
              <span>ESTIMATED GRID LOAD</span>

              <span className="mobile-evidence-tag">
                {isLive ? 'WEATHER-DERIVED' : 'SIMULATED'}
              </span>
            </div>

            <div className="mobile-primary-value-row">
              <div>
                <span className="mobile-primary-value">
                  {gridData?.load || '—'}
                </span>

                <span className="mobile-primary-unit">GW</span>
              </div>

              <div className="mobile-primary-context">
                <span>
                  {gridData?.temperature
                    ? `${gridData.temperature}°C`
                    : '—'}
                </span>

                <small>environmental input</small>
              </div>
            </div>

            <div className="mobile-primary-note">
              Demand estimate derived from the currently available input environment.
              This is not direct national-grid telemetry.
            </div>
          </section>

          <section className={`mobile-system-state ${systemStateClass}`}>
            <div className="mobile-system-state-icon">
              {criticalFaults.length > 0
                ? '!'
                : warningFaults.length > 0
                  ? '△'
                  : '✓'}
            </div>

            <div>
              <span className="mobile-section-kicker">
                SYSTEM STATE
              </span>

              <h2>{systemState}</h2>

              <p>
                {activeFaults.length} active event
                {activeFaults.length === 1 ? '' : 's'} in the current dataset.
              </p>
            </div>
          </section>

          <section className="mobile-command-section">
            <div className="mobile-command-section-header">
              <div>
                <span className="mobile-section-kicker">
                  CURRENT INDICATORS
                </span>
                <h2>System signals</h2>
              </div>

              <span className="mobile-section-meta">AUTO · 5S</span>
            </div>

            <div className="mobile-indicator-grid">
              <div className="mobile-indicator-card">
                <span className="mobile-indicator-label">Solar</span>
                <strong>{gridData?.solar || '—'}</strong>
                <small>MW · estimated</small>
              </div>

              <div className="mobile-indicator-card">
                <span className="mobile-indicator-label">Wind</span>
                <strong>{gridData?.wind || '—'}</strong>
                <small>MW · estimated</small>
              </div>

              <div className="mobile-indicator-card">
                <span className="mobile-indicator-label">Carbon</span>
                <strong>{gridData?.carbon || '—'}</strong>
                <small>gCO₂/kWh</small>
              </div>

              <div className="mobile-indicator-card">
                <span className="mobile-indicator-label">Wind speed</span>
                <strong>{gridData?.windspeed || '—'}</strong>
                <small>km/h · input</small>
              </div>
            </div>
          </section>

          <section className="mobile-insight-card">
            <div className="mobile-insight-header">
              <div>
                <span className="mobile-section-kicker">
                  OPERATIONAL INSIGHT
                </span>
                <h2>What deserves attention</h2>
              </div>

              <span className="mobile-model-tag">
                MODEL INTERPRETATION
              </span>
            </div>

            <p>{getOperationalInsight()}</p>

            <div className="mobile-insight-evidence">
              <div>
                <span>Evidence basis</span>
                <strong>
                  {isLive
                    ? 'Live weather input + model logic'
                    : 'Simulated input + model logic'}
                </strong>
              </div>

              <div>
                <span>Confidence</span>
                <strong>
                  {criticalFaults.length > 0
                    ? 'Event-dependent'
                    : 'Moderate'}
                </strong>
              </div>
            </div>
          </section>

          <section className="mobile-command-section mobile-chart-section">
            <div className="mobile-command-section-header">
              <div>
                <span className="mobile-section-kicker">
                  24-HOUR OUTLOOK
                </span>
                <h2>Load trajectory</h2>
              </div>

              <span className="mobile-evidence-tag amber">
                SIMULATED FORECAST
              </span>
            </div>

            <div className="mobile-chart-wrap">
              <canvas ref={mobileChartRef} />
            </div>

            <div className="mobile-chart-legend">
              <span>
                <i className="green" />
                Demo observed series
              </span>

              <span>
                <i className="amber" />
                Forecast estimate
              </span>
            </div>
          </section>

          <section className="mobile-command-section">
            <div className="mobile-command-section-header">
              <div>
                <span className="mobile-section-kicker">
                  ACTIVE EVENTS
                </span>
                <h2>Conditions requiring review</h2>
              </div>

              <span
                className={`mobile-event-count ${
                  criticalFaults.length > 0
                    ? 'critical'
                    : warningFaults.length > 0
                      ? 'warning'
                      : ''
                }`}
              >
                {activeFaults.length}
              </span>
            </div>

            <div className="mobile-event-list">
              {activeFaults.length > 0 ? (
                activeFaults.slice(0, 3).map((f, index) => (
                  <div
                    className="mobile-event-item"
                    key={`mobile-fault-${f.id ?? 'unknown'}-${f.name ?? 'fault'}-${index}`}
                  >
                    <span
                      className={`mobile-event-severity ${
                        f.severity === 'critical'
                          ? 'critical'
                          : 'warning'
                      }`}
                    />

                    <div className="mobile-event-copy">
                      <strong>{f.name}</strong>
                      <span>{f.location}</span>
                    </div>

                    <div className="mobile-event-meta">
                      <span>{f.time}</span>
                      <small>{f.severity}</small>
                    </div>
                  </div>
                ))
              ) : (
                <div className="mobile-empty-state">
                  <span>✓</span>

                  <div>
                    <strong>No live fault data</strong>
                    <p>
                      Demo environment — no fault feed is connected.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </section>

          <section className="mobile-command-section">
            <div className="mobile-command-section-header">
              <div>
                <span className="mobile-section-kicker">
                  EXTENDED OUTLOOK
                </span>
                <h2>72-hour demand estimate</h2>
              </div>

              <span className="mobile-evidence-tag">
                MODEL ESTIMATE
              </span>
            </div>

            <div className="mobile-forecast-bars">
              {forecastBars.map((b) => {
                const pct = Math.min(
                  Math.round((b.val / 5) * 100),
                  100
                );

                return (
                  <div className="mobile-forecast-row" key={b.label}>
                    <span>{b.label}</span>

                    <div className="mobile-forecast-track">
                      <div
                        className={`mobile-forecast-fill ${
                          pct > 85
                            ? 'critical'
                            : pct > 75
                              ? 'warning'
                              : ''
                        }`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>

                    <strong>{b.val} GW</strong>
                  </div>
                );
              })}
            </div>

            <p className="mobile-method-note">
              Extended values are deterministic estimates derived from the current
              load baseline for demonstration purposes; they are not validated
              operational forecasts.
            </p>
          </section>

          <footer className="mobile-command-footer">
            <span>GRIDMIND · EVIDENCE FIRST</span>

            <p>
              Built by IntelliLearn AI Private Limited · DPIIT-recognised startup · India
            </p>
          </footer>
        </div>
      </div>
    </>
  );
}